import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../contexts/AuthContext';

// ─── Mini bar chart ───────────────────────────────────────────────────────────
function SparkBar({ data = [], labelKey = 'day', valueKey = 'count', color = '#722F37' }) {
  if (!data.length) return <span style={{ color: '#aaa', fontSize: '0.8rem' }}>No data yet</span>;
  const max = Math.max(...data.map((d) => parseInt(d[valueKey]) || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '48px' }}>
      {data.map((d, i) => {
        const val = parseInt(d[valueKey]) || 0;
        const h = Math.max(2, Math.round((val / max) * 48));
        return (
          <div key={i} title={`${d[labelKey]}: ${val}`} style={{
            flex: 1, height: `${h}px`, background: color,
            borderRadius: '2px 2px 0 0', minWidth: '4px',
          }} />
        );
      })}
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function Card({ title, children, style }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #eee', borderRadius: '10px',
      padding: '20px', ...style,
    }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function BigNum({ value, label, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: color || '#1a1a1a' }}>{value ?? '—'}</div>
      {label && <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>{label}</div>}
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ color: '#555', fontSize: '0.9rem' }}>{label}</span>
      <span style={{ fontWeight: 600, color: highlight || '#1a1a1a', fontSize: '0.9rem' }}>{value ?? '—'}</span>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function AdminDashboard({ onClose }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/metrics');
      setMetrics(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Loading metrics…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        <div style={{ textAlign: 'center', padding: '60px', color: '#c0392b' }}>{error}</div>
        <button onClick={onClose} style={styles.closeBtn}>✕</button>
      </div>
    </div>
  );

  const m = metrics;
  const a = m.acquisition;
  const eng = m.engagement;
  const ret = m.retention;
  const rev = m.revenue;
  const act = m.activation;

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1a1a1a' }}>📊 Metrics Dashboard</h2>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>
              {new Date().toLocaleString()} · <button onClick={load} style={{ background: 'none', border: 'none', color: '#722F37', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>Refresh</button>
            </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.grid}>
          {/* Acquisition */}
          <Card title="Acquisition" style={{ gridColumn: 'span 2' }}>
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'space-around', flexWrap: 'wrap' }}>
              <BigNum value={a.total_users} label="Total Users" />
              <BigNum value={a.new_today} label="New Today" color="#27ae60" />
              <BigNum value={a.new_this_week} label="New This Week" color="#2980b9" />
              <BigNum value={a.new_this_month} label="New This Month" color="#8e44ad" />
            </div>
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px' }}>Signups — last 30 days</div>
              <SparkBar data={m.trends.signups_30d} color="#722F37" />
            </div>
            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {a.auth_breakdown.map((b) => (
                <span key={b.method} style={{ fontSize: '0.8rem', background: '#f5f5f5', borderRadius: '6px', padding: '4px 10px' }}>
                  {b.method}: <strong>{b.count}</strong>
                </span>
              ))}
            </div>
          </Card>

          {/* Engagement */}
          <Card title="Engagement">
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'space-around', marginBottom: '12px' }}>
              <BigNum value={eng.dau} label="DAU" color="#e67e22" />
              <BigNum value={eng.wau} label="WAU" color="#e67e22" />
              <BigNum value={eng.mau} label="MAU" color="#e67e22" />
            </div>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px' }}>Daily active users — last 14 days</div>
            <SparkBar data={m.trends.dau_14d} color="#e67e22" />
            <div style={{ marginTop: '12px' }}>
              <Row label="Messages this month" value={eng.messages_this_month?.toLocaleString()} />
              <Row label="Total messages" value={eng.total_messages?.toLocaleString()} />
              <Row label="Menu scans this month" value={eng.scans_this_month} />
            </div>
          </Card>

          {/* Retention */}
          <Card title="Retention">
            <Row label="7-day retention" value={ret.d7_pct !== null ? `${ret.d7_pct}%` : 'N/A'} highlight={ret.d7_pct >= 40 ? '#27ae60' : ret.d7_pct >= 20 ? '#e67e22' : '#c0392b'} />
            <Row label="  ↳ retained / cohort" value={`${ret.d7_retained} / ${ret.d7_cohort}`} />
            <Row label="30-day retention" value={ret.d30_pct !== null ? `${ret.d30_pct}%` : 'N/A'} highlight={ret.d30_pct >= 25 ? '#27ae60' : ret.d30_pct >= 10 ? '#e67e22' : '#c0392b'} />
            <Row label="  ↳ retained / cohort" value={`${ret.d30_retained} / ${ret.d30_cohort}`} />
            <Row label="Deleted accounts (churn)" value={ret.churned_users} highlight={ret.churned_users > 0 ? '#c0392b' : '#27ae60'} />
          </Card>

          {/* Activation & Revenue */}
          <Card title="Activation & Revenue">
            <Row label="On free trial" value={act.trial_active} />
            <Row label="Converted to paid" value={act.plan_converted} />
            <Row label="Premium users" value={rev.premium_users} highlight="#8e44ad" />
            <Row label="AI cost this month" value={`$${rev.cost_this_month_usd}`} highlight="#c0392b" />
          </Card>

          {/* Sommelier levels */}
          <Card title="Sommelier Level Preference">
            {eng.sommelier_levels?.map((l) => (
              <Row key={l.level} label={l.level} value={l.count} />
            ))}
          </Card>

          {/* Top wines */}
          <Card title="Top Liked Wines">
            {m.quality.top_liked_wines?.length > 0
              ? m.quality.top_liked_wines.map((w, i) => (
                <Row key={i} label={w.name} value={`👍 ${w.likes} / net ${w.net_score}`} />
              ))
              : <span style={{ color: '#aaa', fontSize: '0.85rem' }}>No feedback yet</span>
            }
          </Card>

          {/* Violations */}
          <Card title="Scope Violations (7d)">
            <BigNum value={m.quality.scope_violations_7d} color={m.quality.scope_violations_7d > 0 ? '#c0392b' : '#27ae60'} label="off-topic attempts" />
          </Card>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    zIndex: 3000, padding: '20px', overflowY: 'auto',
  },
  panel: {
    background: '#f9f9f9', borderRadius: '14px', width: '100%', maxWidth: '900px',
    padding: '28px', position: 'relative', marginBottom: '20px',
  },
  closeBtn: {
    background: 'none', border: '1px solid #ddd', borderRadius: '6px',
    padding: '4px 10px', cursor: 'pointer', fontSize: '1rem', color: '#666',
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px',
  },
};
